import { LayerSpecification, Map } from "@iventis/mapbox-gl";
import { Tile3DLayer } from "@deck.gl/geo-layers";
import { Tiles3DLoader } from "@loaders.gl/3d-tiles";
import { Polygon } from "geojson";
import bbox from "@turf/bbox";
import { Basemap } from "../basemap";
import { DeckglMapboxIntegration } from "../../deckgl/deckgl-mapbox-integration";
import { Sitemap3dDataStore, Sitemap3d as Sitemap3dType } from "../../../../data-store/basemap/sitemap-3d-data-store";
import { IVENTIS_3D_SITEMAP_WALL_SLOT_ID } from "../../../../bridge/constants";
import { IventisMapboxLayerMetaDataSitemapLayer } from "../../test-helpers/mapbox-test-helper-types";
import { MapboxTerrain } from "../terrain/mapbox-terrain";
import { SitemapVersionLevel } from "../../../../types/store-schema";

export class Sitemap3d extends Basemap<Sitemap3dType, LayerSpecification["type"]> {
    private readonly deckglMapboxIntegration: DeckglMapboxIntegration;

    private readonly wallsLayerId: string;

    private readonly baseLayerId: string;

    private readonly terrainId: string;

    private terrain: MapboxTerrain;

    private readonly bounds: Polygon;

    /** 5 minutes buffer to refresh the sitemap signatures before they expire */
    private readonly refreshSignatureBuffer = 5 * 60 * 1000;

    private signatureExpiry: Date | null = null;

    private isRefreshingSignature = false;

    private readonly timeBetween3dSitemapRenderCheckMs = 500;

    private readonly metaData: IventisMapboxLayerMetaDataSitemapLayer;

    constructor(sitemapVersionLevel: SitemapVersionLevel, sitemapDataStore: Sitemap3dDataStore, map: Map, deckglMapboxIntegration: DeckglMapboxIntegration) {
        super(sitemapVersionLevel.id, sitemapDataStore, map);
        this.deckglMapboxIntegration = deckglMapboxIntegration;
        this.wallsLayerId = `${this.id}-walls`;
        this.baseLayerId = `${this.id}-base`;
        this.terrainId = `${this.id}-terrain`;
        this.bounds = sitemapVersionLevel.bounding;
        this.metaData = {
            sitemapName: sitemapVersionLevel.sitemapName,
            sitemapVersionName: sitemapVersionLevel.sitemapVersionName,
            type: "sitemap",
            levelIndex: sitemapVersionLevel.levelIndex,
        };
        this.addToMap();
    }

    /** Each 3d sitemap has two parts. Walls and the base, this function adds them both to the map */
    protected async addToMap(): Promise<void> {
        const sitemap3dAsset = await this.basemapDataStore.getBasemap(this.id);

        await this.waitForMapToBeReadyToLoadSitemap();

        // As we are awaiting above, before we continue check the sitemap hasn't been removed
        if (this.removed) {
            return;
        }

        this.signatureExpiry = new Date(sitemap3dAsset.expiry);

        const { walls, base, terrain } = this.create3dSitemapUrls(sitemap3dAsset);

        // Base layer to be placed above the map background but below the 2D Sitemap layers
        const baseLayer = new Tile3DLayer({
            id: this.baseLayerId,
            data: base,
            loader: Tiles3DLoader,
            pickable: true,
            loadOptions: {
                "3d-tiles": {
                    isTileset: true,
                },
                fetch: (url: string) => {
                    // Ensure the signautre of the sitemap has not expired
                    this.refreshSitemapSignature();
                    return fetch(url);
                },
            },
            metadata: this.metaData,
        });

        // Walls layer to be placed above the all user layers but below the internal layers such as comments, drawing handles
        const wallsLayer = new Tile3DLayer({
            id: this.wallsLayerId,
            data: walls,
            beforeId: IVENTIS_3D_SITEMAP_WALL_SLOT_ID,
            loader: Tiles3DLoader,
            pickable: true,
            loadOptions: {
                "3d-tiles": {
                    isTileset: true,
                },
                fetch: (url: string) => {
                    // Ensure the signautre of the sitemap has not expired
                    this.refreshSitemapSignature();
                    return fetch(url);
                },
            },
            metadata: this.metaData,
        });

        this.terrain = new MapboxTerrain(this.map, this.terrainId, terrain, bbox(this.bounds) as [number, number, number, number]);

        this.deckglMapboxIntegration.addLayerToExternalOverlay(baseLayer);
        this.deckglMapboxIntegration.addLayerToInterleavedOverlay(wallsLayer);

        // Ensures Mapbox starts to render the 3d sitemap
        if (this.map.idle()) {
            this.map.triggerRepaint();
        } else {
            this.map.once("idle", () => {
                this.map.triggerRepaint();
            });
        }

        this.waitFor3dSitemapToRender();
    }

    private create3dSitemapUrls(sitemap3d: Sitemap3dType) {
        const baseUrl = sitemap3d?.url?.split("?")?.[0];

        if (baseUrl == null) {
            throw new Error(`Could not get baseurl for 3d sitemap with id ${sitemap3d?.id}`);
        }

        return {
            walls: `${baseUrl}/walls/tileset.json?${sitemap3d.signature}&assetVersion=${sitemap3d.updatedAt.toISOString()}`,
            base: `${baseUrl}/base/tileset.json?${sitemap3d.signature}&assetVersion=${sitemap3d.updatedAt.toISOString()}`,
            terrain: `${baseUrl}/terrain/{z}/{x}/{y}.png?${sitemap3d.signature}&assetVersion=${sitemap3d.updatedAt.toISOString()}`,
        };
    }

    private async refreshSitemapSignature() {
        // Is not refreshing the current and the current source is expired
        if (!this.isRefreshingSignature && this.signatureExpiry.getTime() - this.refreshSignatureBuffer < new Date().getTime()) {
            this.isRefreshingSignature = true;
            // Set new expiry time and set new timeout for next signature refresh
            this.basemapDataStore.bustBasemapById(this.id);
            this.removeLayers();
            await this.addToMap();
            this.isRefreshingSignature = false;
        }
    }

    private removeLayers() {
        this.deckglMapboxIntegration.removeLayer(this.baseLayerId);
        this.deckglMapboxIntegration.removeLayer(this.wallsLayerId);
    }

    private async waitFor3dSitemapToRender() {
        await new Promise<void>((res) => {
            setTimeout(() => {
                res();
            }, this.timeBetween3dSitemapRenderCheckMs);
        });

        this.loaded = this.deckglMapboxIntegration.isLayerLoaded(this.baseLayerId) && this.deckglMapboxIntegration.isLayerLoaded(this.wallsLayerId);
        if (!this.loaded) {
            this.waitFor3dSitemapToRender();
        }
    }

    public remove(): void {
        super.remove();
        this.removeLayers();
        this.terrain?.remove();
    }
}
